import { searchLogger, searchErrorLogger } from '../src/core/Logger';
import { createApp, ref } from 'vue';
import AutocompletePopup from './components/AutocompletePopup/AutocompletePopup.vue';
import SearchInput from './components/AutocompletePopup/AutocompleteInputContainer.vue';
import SerpPage from './components/SerpPage/SerpPage.vue';

import { SmartSearchConfigInterface, SmartSearchTranslationsInterface } from './core/types';
import {
  restoreDefaultSettings,
  fetchFrontSettings,
  searchIsConfigured,
  deepAssign,
  showAsStylisationList
} from './core/utils';
import { createPinia } from 'pinia';
import DefaultConfig from '../public/config/DefaultConfig.json';
import DefaultLabels from '../public/labels/DefaultLabels.json';

import VueLazyload from 'vue-lazyload';
import ClickOutsideDirective from "@/core/ClickOutsideDirective";

const { VUE_APP_VERSION } = process.env;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const SmartSearchConfig = ref<SmartSearchConfigInterface>(DefaultConfig);
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const SmartSearchTranslations = ref<SmartSearchTranslationsInterface>(DefaultLabels);
searchLogger.log(`Version: ${VUE_APP_VERSION}`);
const pinia = createPinia();

function isMounted(selector: string): boolean {
  const element = document.querySelector(selector);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return element && element.__vue_app__ !== undefined;
}

async function initializeAutocomplete(selector = '#smartsearch-autocomplete') {
  searchLogger.log('initialising autocomplete');
  const autocompleteApp = createApp(AutocompletePopup);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  autocompleteApp.component('searchInput', SmartSearchConfig.value.serp.customComponents.topProduct);
  autocompleteApp.directive('click-outside', ClickOutsideDirective);
  autocompleteApp.provide('SmartSearchConfig', SmartSearchConfig);
  autocompleteApp.provide('SmartSearchTranslations', SmartSearchTranslations);
  autocompleteApp.use(pinia);
  autocompleteApp.use(VueLazyload);
  autocompleteApp.mount(selector);
  
  setTimeout(() => {
    if (!isMounted(selector)) {
      searchLogger.log('Retrying to mount Autocomplete after 300ms');
      autocompleteApp.mount(selector);
    }
  }, 300);
  
  if (isMounted(selector)) {
    searchLogger.log('Autocomplete mounted successfully');
  } else {
    searchErrorLogger.log('Failed to mount Autocomplete');
  }
}

async function initializeSearchInput(selector = '#smartsearch-searchInput') {
  searchLogger.log('initialising search input');
  const SearchInputApp = createApp(SearchInput);
  SearchInputApp.directive('click-outside', ClickOutsideDirective);
  SearchInputApp.provide('SmartSearchConfig', SmartSearchConfig);
  SearchInputApp.provide('SmartSearchTranslations', SmartSearchTranslations);
  SearchInputApp.use(pinia);
  SearchInputApp.use(VueLazyload);
  SearchInputApp.mount(selector);
  
  setTimeout(() => {
    if (!isMounted(selector)) {
      searchLogger.log('Retrying to mount SearchInput after 300ms');
      SearchInputApp.mount(selector);
    }
  }, 300);
  
  if (isMounted(selector)) {
    searchLogger.log('SearchInput mounted successfully');
  } else {
    searchErrorLogger.log('Failed to mount SearchInput');
  }
}

async function initializeSerpPage(selector = '#smartsearch-serp') {
  searchLogger.log('initialising Serp');
  const serpApp = createApp(SerpPage);
  serpApp.directive('click-outside', ClickOutsideDirective);
  serpApp.provide('SmartSearchConfig', SmartSearchConfig);
  serpApp.provide('SmartSearchTranslations', SmartSearchTranslations);
  serpApp.use(pinia);
  serpApp.use(VueLazyload);
  serpApp.mount(selector);
  
  setTimeout(() => {
    if (!isMounted(selector)) {
      searchLogger.log('Retrying to mount SerpPage after 300ms');
      serpApp.mount(selector);
    }
  }, 300);
  
  if (isMounted(selector)) {
    searchLogger.log('SerpPage mounted successfully');
  } else {
    searchErrorLogger.log('Failed to mount SerpPage');
  }
}

async function retryMountOnLoad() {
  if (!isMounted('#smartsearch-autocomplete')) {
    searchLogger.log('Retrying to mount Autocomplete on DOMContentLoaded');
    await initializeAutocomplete();
  }
  if (!isMounted('#smartsearch-searchInput')) {
    searchLogger.log('Retrying to mount SearchInput on DOMContentLoaded');
    await initializeSearchInput();
  }
  if (!isMounted('#smartsearch-serp')) {
    searchLogger.log('Retrying to mount SerpPage on DOMContentLoaded');
    await initializeSerpPage();
  }
}

document.addEventListener('DOMContentLoaded', retryMountOnLoad);

(async () => {
  if (!searchIsConfigured()) {
    searchErrorLogger.log('SmartSearch needs config in window.SmartSearchConfig');
    return;
  }
  
  const frontSettings = await fetchFrontSettings();
  const { settings, labels } = frontSettings;
  
  const mergedConfig = deepAssign()(DefaultConfig, settings);
  const mergedLabels = deepAssign()(DefaultLabels, labels);
  
  SmartSearchTranslations.value = mergedLabels as SmartSearchTranslationsInterface;
  SmartSearchConfig.value = mergedConfig as SmartSearchConfigInterface;
  restoreDefaultSettings();
  showAsStylisationList();
  
  searchLogger.log('SmartSearchTranslations: ', SmartSearchTranslations.value);
  searchLogger.log('SmartSearchConfig: ', SmartSearchConfig.value);
  
  await initializeAutocomplete();
  await initializeSearchInput();
  await initializeSerpPage();
})();

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window.SmartSearchInstanceConfig = SmartSearchConfig.value;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer = window._QONizer || {};
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeAutocomplete = (selector = '#smartsearch-autocomplete') => initializeAutocomplete(selector);
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeSerpPage = (selector = '#smartsearch-serp') => initializeSerpPage(selector);
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeSearchInput = (selector = '#smartsearch-searchInput') => initializeSearchInput(selector);

export { SmartSearchConfig, initializeAutocomplete, initializeSearchInput, initializeSerpPage };
